import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Chip, Input, Toggle } from 'common/components';
import type { ChipColor } from 'common/components';
import { Table, TableColumn } from 'common/components/Table';
import { useAuth } from 'contexts/AuthContext';
import { useGetCompanies } from 'services/hooks/queries';
import { useSetCompanyProduct } from 'services/hooks/mutations';
import type { Company, CompanyProduct, Product } from 'types/accessManager.type';
import { PRODUCT_LABELS, PRODUCT_ORDER } from 'pages/AccessManager/products';
import styles from './CompanyProducts.module.scss';

const STATUS_LABELS: Record<string, string> = {
    ACTIVE: 'Active',
    ONBOARDING: 'Onboarding',
    PAST_DUE: 'Past due',
    SUSPENDED: 'Suspended',
    OFF: 'Off',
};

const statusColor = (p: CompanyProduct): ChipColor => {
    if (p.enabled) return 'success';
    return p.status === 'OFF' ? 'secondary' : 'warning';
};

// Which products every company has, switched here (SLAI staff only). Products
// are independent; switching one on gives nobody a role in it — the company's
// owner hands them out in Access Manager. A product the backend does not mark
// switchable is shown as its status.
const CompanyProducts: React.FC = () => {
    const { userRoles } = useAuth();
    // Same check as TenantConfigViewer, so a role in another case still counts.
    const isSuperAdmin = (Array.isArray(userRoles) ? userRoles : [])
        .map((r) => (typeof r === 'string' ? r.toUpperCase() : String(r)))
        .includes('SUPER_ADMIN');
    const { companiesData, isCompaniesLoading, isError } = useGetCompanies(isSuperAdmin);
    const { setCompanyProduct } = useSetCompanyProduct();
    const [filter, setFilter] = useState('');
    // Every switch in flight, not just the last: two toggles at once must keep
    // both disabled until each request has answered.
    const [switching, setSwitching] = useState<ReadonlySet<string>>(new Set());

    const companies = useMemo(() => {
        const all = companiesData?.companies ?? [];
        const f = filter.trim().toLowerCase();
        return f ? all.filter((c) => c.name.toLowerCase().includes(f)) : all;
    }, [companiesData, filter]);

    if (!isSuperAdmin) {
        return <Navigate to='/dashboard' replace />;
    }

    const toggle = async (company: Company, product: Product, enabled: boolean) => {
        const key = `${company.id}:${product}`;
        setSwitching((current) => new Set(current).add(key));
        try {
            await setCompanyProduct({ accountId: company.id, product, enabled });
        } catch (error) {
            // The API layer shows the error.
            console.error('Error switching product:', error);
        } finally {
            setSwitching((current) => {
                const next = new Set(current);
                next.delete(key);
                return next;
            });
        }
    };

    const productColumn = (product: Product): TableColumn<Company> => ({
        key: product,
        title: PRODUCT_LABELS[product],
        render: (_, company) => {
            const p = company.products[product];
            if (!p) return <span>—</span>;
            if (p.switchable) {
                return (
                    <div className={styles.switch}>
                        <Toggle
                            aria-label={`${PRODUCT_LABELS[product]} for ${company.name}`}
                            checked={p.enabled}
                            disabled={switching.has(`${company.id}:${product}`)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                toggle(company, product, e.target.checked)
                            }
                        />
                        <span>{p.enabled ? 'On' : 'Off'}</span>
                    </div>
                );
            }
            return (
                <Chip variant='filled' color={statusColor(p)} size='sm'>
                    {STATUS_LABELS[p.status] ?? p.status}
                </Chip>
            );
        },
    });

    const columns: TableColumn<Company>[] = [
        { key: 'name', title: 'Company', dataIndex: 'name' },
        ...PRODUCT_ORDER.map(productColumn),
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Company Products</h1>
            </div>
            <p className={styles.note}>
                Products are switched on and off one by one; they are independent. Switching a product on gives nobody a
                role in it: the company&apos;s owner assigns roles in Access Manager. A new sign-up starts with Agent
                Builder only.
            </p>
            <div className={styles.filter}>
                <Input
                    type='text'
                    placeholder='Filter by company name'
                    value={filter}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
                />
            </div>
            {isError && (
                <p className={styles.error} role='alert'>
                    The companies could not be loaded. Reload the page to try again.
                </p>
            )}
            <Table
                data={companies}
                columns={columns}
                isLoading={isCompaniesLoading}
                emptyText={isError ? 'Nothing to show' : 'No companies found'}
                rowKey='id'
                hoverable
                variant='secondary'
            />
        </div>
    );
};

export default CompanyProducts;
